/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import { Vue } from 'vue-property-decorator';
import router from '@/router';
import AgreementsPop from './AgreementsPop.vue';
import type { AgreementsPopProps, OpenParams, OpenResult } from './types';
import { AgreementsPopEvents } from './event-names';

/**
 * AgreementsPop 组件的适配器类
 * 将 Vue 组件实例包装成单例模式
 */
class AgreementsPopAdapter {
  private wrapperInstance: Vue;

  private resolve?: (value: OpenResult) => void;

  private reject?: () => void;

  private agreementConfirmed = false;

  constructor() {
    // 保存回调引用，用于在 render 函数中使用
    const onUpdateShow = this.handleUpdateShow.bind(this);
    const onAgreementConfirmed = this.handleAgreementConfirmed.bind(this);
    const onClose = this.handleClose.bind(this);
    const onCancel = this.handleCancel.bind(this);

    // 创建包装器 Vue 实例，用于管理响应式状态
    this.wrapperInstance = new Vue({
      router,
      data() {
        return {
          show: false,
          agreementList: [],
          isLoading: false,
          hasCreditRouteChannelAgreement: false,
        };
      },
      render(h) {
        const vm = this as Vue & AgreementsPopProps;
        return h(AgreementsPop, {
          props: {
            show: vm.show,
            agreementList: vm.agreementList,
            isLoading: vm.isLoading,
            hasCreditRouteChannelAgreement: vm.hasCreditRouteChannelAgreement,
          },
          on: {
            [AgreementsPopEvents.UPDATE_SHOW]: onUpdateShow,
            [AgreementsPopEvents.AGREEMENT_CONFIRMED]: onAgreementConfirmed,
            [AgreementsPopEvents.CLOSE]: onClose,
            [AgreementsPopEvents.CANCEL]: onCancel,
          },
        });
      },
    });

    // 挂载到 DOM
    const container = document.createElement('div');
    document.body.appendChild(container);
    this.wrapperInstance.$mount(container);
  }

  /**
   * 处理弹窗显示状态更新
   */
  private handleUpdateShow(value: boolean): void {
    const vm = this.wrapperInstance.$data as { show: boolean };
    vm.show = value;
  }

  /**
   * 处理协议阅读完成事件
   */
  private handleAgreementConfirmed(): void {
    this.agreementConfirmed = true;
    this.close();
    if (this.resolve) {
      this.resolve(true);
    }
  }

  /**
   * 处理关闭事件
   */
  private handleClose(): void {
    this.close();
    if (!this.agreementConfirmed) {
      if (this.reject) {
        this.reject();
      }
    }
  }

  /**
   * 处理取消事件
   */
  private handleCancel(): void {
    this.close();
    if (this.reject) {
      this.reject();
    }
  }

  /**
   * 打开弹窗
   */
  public open(params: OpenParams): Promise<OpenResult> {
    // 重置状态
    this.agreementConfirmed = false;
    // 使用 Vue 的响应式数据来控制显示
    const vm = this.wrapperInstance.$data as AgreementsPopProps;

    this.wrapperInstance.$set(vm, 'show', true);
    this.wrapperInstance.$set(vm, 'agreementList', params.agreementList || []);
    this.wrapperInstance.$set(vm, 'isLoading', params.isLoading || false);
    this.wrapperInstance.$set(vm, 'hasCreditRouteChannelAgreement', params.hasCreditRouteChannelAgreement || false);

    return new Promise<OpenResult>((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }

  /**
   * 更新弹窗参数（仅更新协议列表和数据是否加载中）
   */
  public updateParams(params: OpenParams): void {
    const vm = this.wrapperInstance.$data as AgreementsPopProps;

    // 使用 Vue.set 确保响应式更新
    this.wrapperInstance.$set(vm, 'agreementList', params.agreementList || []);
    this.wrapperInstance.$set(vm, 'isLoading', params.isLoading || false);
    this.wrapperInstance.$set(vm, 'hasCreditRouteChannelAgreement', params.hasCreditRouteChannelAgreement || false);
  }

  /**
   * 关闭弹窗
   */
  public close(): void {
    // 使用 Vue 的响应式数据来控制隐藏
    const vm = this.wrapperInstance.$data as { show: boolean };
    vm.show = false;
  }
}

let instance: AgreementsPopAdapter;

/**
 * 单例模式打开弹窗
 */
export const agreementsPopService = {
  /**
   * 打开弹窗
   * @param params 打开参数
   * @returns Promise，resolve 时返回结果，reject 时表示用户取消或关闭
   */
  open(params: OpenParams): Promise<boolean> {
    if (!instance) {
      instance = new AgreementsPopAdapter();
    }

    return instance.open(params);
  },

  /**
   * 更新弹窗参数（仅更新协议列表和数据是否加载中）
   */
  updateParams(params: OpenParams): void {
    if (instance) {
      instance.updateParams(params);
    }
  },

  /**
   * 关闭弹窗
   */
  close(): void {
    if (instance) {
      instance.close();
    }
  },
};
